import {Box, Stack, Typography, Alert} from '@mui/material'
import React, {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import MainBtn from '../../customTable/MainBtn'
import InputCode from '../../login/InputCode'
import {formatPaymentDisplay} from '../../../utils/paymentHelpers'

const PRIMARY = '#2C3E6B'
const MUTED = '#65656A'
const SURFACE = '#F0F3F7'
const BORDER = '#E5E7EB'
const OTP_TTL_SECONDS = 120 // Match PlatformOtpService TTL_MINUTES = 2
const OTP_KEYS = ['a', 'b', 'c', 'd', 'e', 'f'] as const

type Props = {
  signature: string
  amountLabel?: string
  amount?: number | string
  currency?: string
  planName?: string
  titleKey?: string
  isLoading?: boolean
  onSubmit: (code: string) => void
  onCancel: () => void
  onExpired?: () => void
  onResend?: () => void
  isResending?: boolean
}

function formatTimer(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

/**
 * Platform OTP modal — 6-digit code, 60s timer.
 * Summary (plan + price) shown once at bottom.
 */
export default function OTPModal({
  signature,
  amountLabel,
  amount,
  currency,
  planName,
  titleKey = 'verifyYourCode',
  isLoading = false,
  onSubmit,
  onCancel,
  onExpired,
  onResend,
  isResending = false,
}: Props) {
  const {t} = useTranslation()
  const [codeValue, setCodeValue] = useState<Record<string, string>>({})
  const [isfocus, setFoucus] = useState('a')
  const [errorMsg, setErrorMsg] = useState('')
  const [secondsLeft, setSecondsLeft] = useState(OTP_TTL_SECONDS)

  void signature

  const displayAmount =
    formatPaymentDisplay(amountLabel || amount, currency) ||
    (amountLabel && !/\bundefined\b/i.test(amountLabel) ? amountLabel : '')

  useEffect(() => {
    setSecondsLeft(OTP_TTL_SECONDS)
    setCodeValue({})
    setErrorMsg('')
    setFoucus('a')
  }, [signature])

  useEffect(() => {
    if (secondsLeft <= 0) {
      onExpired?.()
      return
    }

    const timer = window.setTimeout(() => {
      setSecondsLeft((prev) => prev - 1)
    }, 1000)

    return () => window.clearTimeout(timer)
  }, [secondsLeft, onExpired])

  const isExpired = secondsLeft <= 0

  const buildCode = (): string => OTP_KEYS.map((key) => codeValue[key] || '').join('')

  const handleSubmit = () => {
    if (isExpired || isLoading) {
      return
    }

    const code = buildCode()
    if (code.length !== 6 || !/^\d{6}$/.test(code)) {
      setErrorMsg(String(t('otpInvalidCode') || 'Enter the full 6-digit code'))
      return
    }

    setErrorMsg('')
    onSubmit(code)
  }

  const handleResend = () => {
    if (!onResend || isResending) {
      return
    }
    onResend()
    setSecondsLeft(OTP_TTL_SECONDS)
    setCodeValue({})
    setErrorMsg('')
    setFoucus('a')
  }

  return (
    <Stack gap={{xs: '16px', sm: '20px'}} width='100%'>
      <Stack alignItems='center' gap='8px'>
        <Typography
          sx={{
            fontSize: {xs: '17px', sm: '18px'},
            fontWeight: 700,
            color: PRIMARY,
            textAlign: 'center',
          }}
        >
          {t(titleKey) || t('confirmCheckout') || 'Verify your code'}
        </Typography>
        <Typography sx={{fontSize: {xs: '13px', sm: '14px'}, color: MUTED, textAlign: 'center'}}>
          {t('otpSmsHint') || t('otpEmailHint') || 'Enter the 6-digit WhatsApp code'}
        </Typography>
      </Stack>

      <Typography
        sx={{
          fontSize: {xs: '22px', sm: '24px'},
          fontWeight: 700,
          color: isExpired ? '#f16645' : PRIMARY,
          textAlign: 'center',
          letterSpacing: '0.04em',
        }}
      >
        {isExpired
          ? t('otpExpired') || 'Code expired'
          : `⏱ ${(t('otpExpiresIn') || 'Code expires in').replace(/\s*$/, '')} ${formatTimer(secondsLeft)}`}
      </Typography>

      {isExpired ? (
        <Alert severity='warning' sx={{width: '100%'}}>
          {t('otpExpiredHint') || 'Request a new verification code to continue.'}
        </Alert>
      ) : null}

      {errorMsg ? <Alert severity='error'>{errorMsg}</Alert> : null}

      <Stack
        direction='row'
        gap={{xs: '6px', sm: '10px'}}
        justifyContent='center'
        flexWrap='nowrap'
        sx={{overflowX: 'auto', maxWidth: '100%', py: '4px'}}
      >
        {OTP_KEYS.map((key) => (
          <InputCode
            key={key}
            err={false}
            isfocus={isfocus}
            codeValue={codeValue}
            e={key}
            setCodeValue={setCodeValue}
            setFoucus={setFoucus}
            arrCode={[...OTP_KEYS]}
          />
        ))}
      </Stack>

      {(planName || displayAmount) && (
        <Box
          sx={{
            width: '100%',
            borderTop: `1px solid ${BORDER}`,
            borderBottom: `1px solid ${BORDER}`,
            py: '12px',
            px: '4px',
          }}
        >
          <Stack direction='row' justifyContent='space-between' alignItems='center' gap='12px'>
            <Typography
              sx={{
                fontSize: {xs: '14px', sm: '15px'},
                fontWeight: 600,
                color: PRIMARY,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {planName || '—'}
            </Typography>
            <Typography
              sx={{
                fontSize: {xs: '16px', sm: '18px'},
                fontWeight: 700,
                color: PRIMARY,
                whiteSpace: 'nowrap',
              }}
            >
              {displayAmount}
            </Typography>
          </Stack>
        </Box>
      )}

      <Stack
        direction={{xs: 'column', sm: 'row'}}
        gap='12px'
        justifyContent='center'
        alignItems='stretch'
        width='100%'
      >
        {onResend ? (
          <MainBtn
            width={{xs: '100%', sm: 'auto'}}
            minWidth={{xs: '100%', sm: '110px'}}
            height='48px'
            fs='14px'
            without
            isLoading={isResending}
            handelSubmit={handleResend}
            text='resendCode'
          />
        ) : null}
        <MainBtn
          width={{xs: '100%', sm: 'auto'}}
          minWidth={{xs: '100%', sm: '110px'}}
          height='48px'
          fs='14px'
          without
          handelSubmit={onCancel}
          text='cancel'
        />
        <MainBtn
          width={{xs: '100%', sm: 'auto'}}
          minWidth={{xs: '100%', sm: '130px'}}
          height='48px'
          fs='16px'
          isLoading={isLoading}
          disabled={isExpired}
          handelSubmit={handleSubmit}
          text='verify'
          bgcolor={PRIMARY}
        />
      </Stack>
    </Stack>
  )
}
